import FilterSelect from 'common/components/FilterSelect'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Alert, Badge, Table} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {getCsvImportSchema} from 'setup/redux/Actions/CsvImport/csvImport'

interface MatchInfo {
  table: string
  dbColumn: string
  similarity: number
  matchType:
    | 'exact'
    | 'normalized'
    | 'synonym'
    | 'pattern'
    | 'token'
    | 'partial'
    | 'similarity'
    | 'manual'
    | 'ai'
  confidence: 'high' | 'medium' | 'low'
  reasoning?: string
}

interface TableSchemas {
  [table: string]: string[]
}

interface TableMappings {
  [table: string]: {[key: string]: string}
}

interface ForeignKeyOption {
  id: number
  name: string
  firstName?: string
  lastName?: string
  cnic?: string
  [key: string]: any
}

interface ForeignKeyOptions {
  [key: string]: ForeignKeyOption[]
}

interface CsvColumnMapperProps {
  csvColumns: string[]
  columnMappings: {[csvColumn: string]: string}
  columnMatches: {[key: string]: MatchInfo}
  schemas: TableSchemas
  selectedTable: string | null
  tableMappings: TableMappings
  warnings?: string[]
  foreignKeyOptions?: ForeignKeyOptions
  showRequiredMissingData?: boolean
  /** A branch with a single choice is picked silently and named in one line (no card). */
  compactSingleBranch?: boolean
  onColumnMappingChange: (csvColumn: string, dbColumn: string) => void
  onDefaultValuesChange?: (selectedMappings: {[key: string]: any}) => void
  onHasUnresolvedColumns?: (hasUnresolved: boolean) => void
}

const COLUMN_DISPLAY_NAMES: {[key: string]: string} = {
  'branch.branch.name': 'Branch Name',
  'branch.branch.address': 'Branch Address',
  'branch.branch.phone': 'Branch Phone',
  'class.class.name': 'Class Name',
  'class.class.status': 'Class Status',
  'class_room.class_room.userId': 'Class Room ID',
  'class_room.class_room.name': 'Class Room Name',
  'grades.grades.name': 'Grade Name',
  'grades.grades.status': 'Grade Status',
  'levels.levels.name': 'Level Name',
  'sections.sections.name': 'Section Name',
  'subject.subject.name': 'Subject Name',
  'subject.subject.description': 'Subject Description',
  'subject.subject.status': 'Subject Status',
  'subject.subject.frequency': 'Subject Frequency',
  'subject.subject.lecture_type': 'Lecture Type',
  'subject.subject.duration': 'Duration',
  'user.user.username': 'Username',
  'user.user.email': 'Email',
  'user.user.type': 'User Type',
  'user.user.gender': 'Gender',
  'user.user.dob': 'Date of Birth',
  'user.user.userStatus': 'User Status',
  'user_parent.user_parent.firstName': 'Parent First Name',
  'user_parent.user_parent.lastName': 'Parent Last Name',
  'user_parent.user_parent.address': 'Parent Address',
  'user_parent.user_parent.phone': 'Parent Phone',
  'user_parent.user_parent.cnic': 'Parent CNIC',
  'user_parent.user_parent.mobile': 'Parent Mobile',
  'user_student.user_student.firstName': 'Student First Name',
  'user_student.user_student.lastName': 'Student Last Name',
  'user_student.user_student.studentno': 'Student Registration Number',
  'user_student.user_student.address': 'Student Address',
  'user_student.user_student.permanent_address': 'Permanent Address',
  'user_student.user_student.phone': 'Student Phone',
  'user_student.user_student.mobile': 'Student Mobile',
  'user_student.user_student.registrationNo': 'Registration No',
  'user_student.user_student.religion': 'Religion',
  'user_student.user_student.place_of_birth': 'Place of Birth',
  'user_student.user_student.nationality': 'Nationality',
  'user_student.user_student.cnic': 'Student CNIC',
  'student_subject.student_subject.studentId': 'Student ID',
  'student_subject.student_subject.subjectId': 'Subject ID',
  'user_teacher.user_teacher.firstName': 'Teacher Name',
  'user_teacher.user_teacher.lastName': 'Teacher Last Name',
  'user_teacher.user_teacher.employeeId': 'Teacher Employee ID',
  'user_teacher.user_teacher.address': 'Teacher Address',
  'user_teacher.user_teacher.phone': 'Teacher Phone',
  'user_teacher.user_teacher.cnic': 'Teacher CNIC',
  'user_teacher.user_teacher.mobile': 'Teacher Mobile',
}

const getColumnDisplayName = (columnKey: string): string => {
  return COLUMN_DISPLAY_NAMES[columnKey] || columnKey
}

const CsvColumnMapper: React.FC<CsvColumnMapperProps> = ({
  csvColumns,
  columnMappings,
  columnMatches,
  schemas,
  selectedTable,
  warnings = [],
  foreignKeyOptions = {},
  showRequiredMissingData = true,
  compactSingleBranch = false,
  onColumnMappingChange,
  onDefaultValuesChange,
  onHasUnresolvedColumns,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [apiSchemaColumns, setApiSchemaColumns] = useState<string[]>([])
  const [selectedDefaults, setSelectedDefaults] = useState<{[key: string]: any}>({})
  const onDefaultValuesChangeRef = useRef(onDefaultValuesChange)
  const onHasUnresolvedColumnsRef = useRef(onHasUnresolvedColumns)

  // Update the refs whenever the callbacks change
  useEffect(() => {
    onDefaultValuesChangeRef.current = onDefaultValuesChange
  }, [onDefaultValuesChange])
  useEffect(() => {
    onHasUnresolvedColumnsRef.current = onHasUnresolvedColumns
  }, [onHasUnresolvedColumns])

  // Memoize missing columns extraction to avoid recalculation on every render
  const missingColumns = useMemo(() => {
    const columns: string[] = []
    warnings.forEach((warning) => {
      // Support multiple warning patterns for better flexibility
      const patterns = [
        /Required column (\w+) is missing/,
        /Missing required column[:\s]+(\w+)/,
        /Column (\w+) is required but not found/,
        /(\w+) column is missing/,
      ]

      let foundMatch = false
      for (const pattern of patterns) {
        const match = warning.match(pattern)
        if (match && match[1] && !columns.includes(match[1])) {
          columns.push(match[1])
          foundMatch = true
          break
        }
      }

      // If no pattern matched and it's a simple string (like "grades"),
      // check if it exists in foreignKeyOptions and treat it as a column name
      if (
        !foundMatch &&
        typeof warning === 'string' &&
        warning.trim() &&
        !columns.includes(warning)
      ) {
        // Only add if it has foreign key options available or is a simple column name
        if (foreignKeyOptions[warning] || /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(warning)) {
          columns.push(warning)
        }
      }
    })
    return columns
  }, [warnings, foreignKeyOptions])

  // Format column name for display
  const formatColumnName = useCallback((column: string) => {
    return column
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim()
  }, [])

  // Helpers to identify branch/level-like column names
  const isBranchColumnName = useCallback((name: string) => {
    const n = name?.toLowerCase?.() || ''
    return n === 'branch' || n === 'branches' || n.includes('branch')
  }, [])

  const isLevelColumnName = useCallback((name: string) => {
    const n = name?.toLowerCase?.() || ''
    return n === 'level' || n === 'levels' || n === 'grades' || n.includes('level')
  }, [])

  // `compactSingleBranch`: a branch column with one choice is picked without asking — no
  // "Branch Selection Required" card, one line naming the branch instead.
  const autoBranchColumns = compactSingleBranch
    ? missingColumns.filter(
        (column) => isBranchColumnName(column) && (foreignKeyOptions[column] || []).length === 1
      )
    : []
  const askedColumns = missingColumns.filter((column) => !autoBranchColumns.includes(column))

  const getSelectedBranchId = useCallback(
    (defaults: {[key: string]: any}) => {
      const entry = Object.entries(defaults || {}).find(([key]) => isBranchColumnName(key))
      const selected = entry ? entry[1] : undefined
      return selected?.id
    },
    [isBranchColumnName]
  )

  // Format option display name - concatenate firstName and lastName for teachers
  const formatOptionName = useCallback((option: ForeignKeyOption, column: string) => {
    // Check if this is a teacher-related column
    const isTeacherColumn =
      column.toLowerCase().includes('teacher') || column.toLowerCase().includes('user_teacher')

    if (isTeacherColumn && option.firstName && option.lastName) {
      return `${option.firstName} ${option.lastName}`
    }

    // For other cases, use the name property or fallback
    return (
      option.name ||
      `${option.firstName || ''} ${option.lastName || ''}`.trim() ||
      `Option ${option.id}`
    )
  }, [])

  // Check if a missing column is resolved through column mapping
  const isMissingColumnMapped = useCallback(
    (column: string) => {
      const mappedValues = Object.values(columnMappings).filter((v) => v !== '')
      return mappedValues.some((val) => {
        const parts = val.split('.')
        const lastPart = parts[parts.length - 1]
        return lastPart.toLowerCase() === column.toLowerCase()
      })
    },
    [columnMappings]
  )

  // Report unresolved required columns (missing + no FK options + not mapped from input)
  useEffect(() => {
    if (!onHasUnresolvedColumnsRef.current) return
    const noOptionColumns = missingColumns.filter(
      (col) => !foreignKeyOptions[col] || foreignKeyOptions[col].length === 0
    )
    const hasUnresolved = noOptionColumns.some((col) => !isMissingColumnMapped(col))
    onHasUnresolvedColumnsRef.current(hasUnresolved)
  }, [missingColumns, foreignKeyOptions, isMissingColumnMapped])

  const handleSelectionChange = useCallback(
    (column: string, option: ForeignKeyOption) => {
      setSelectedDefaults((prev) => {
        const nextDefaults: {[key: string]: any} = {
          ...prev,
          [column]: option,
        }

        // If branch selection changed, clear incompatible level selections
        if (isBranchColumnName(column)) {
          const selectedBranchId = option?.id
          Object.keys(prev).forEach((key) => {
            if (isLevelColumnName(key)) {
              const currentLevel = prev[key]
              const belongsToBranch =
                currentLevel?.branch_id === selectedBranchId ||
                currentLevel?.branchId === selectedBranchId ||
                currentLevel?.branch === selectedBranchId ||
                currentLevel?.branch?.id === selectedBranchId
              if (!belongsToBranch) {
                delete nextDefaults[key]
              }
            }
          })
        }

        return nextDefaults
      })
    },
    [isBranchColumnName, isLevelColumnName]
  )

  // Initialize defaults; branch first, then filter levels by selected branch
  useEffect(() => {
    if (missingColumns.length === 0) return

    const defaults: {[key: string]: any} = {}

    // Pick branch first if present
    const branchKey = missingColumns.find((c) => isBranchColumnName(c))
    if (branchKey) {
      const branchOptions = foreignKeyOptions[branchKey] || []
      if (branchOptions.length > 0) {
        defaults[branchKey] = branchOptions[0]
      }
    }

    const selectedBranchId = getSelectedBranchId(defaults)

    // Initialize others; filter level options by branch if branch selected
    missingColumns.forEach((column) => {
      const options = foreignKeyOptions[column] || []
      if (!options.length) return

      if (isLevelColumnName(column) && selectedBranchId) {
        const filtered = options.filter(
          (opt) =>
            opt?.branch_id === selectedBranchId ||
            opt?.branchId === selectedBranchId ||
            opt?.branch === selectedBranchId ||
            opt?.branch?.id === selectedBranchId
        )
        if (filtered.length > 0) {
          defaults[column] = filtered[0]
        }
      } else if (!defaults[column]) {
        defaults[column] = options[0]
      }
    })

    setSelectedDefaults((prev) => {
      const changed = Object.keys(defaults).some((k) => !prev[k] || prev[k].id !== defaults[k]?.id)
      if (changed) {
        return defaults
      }
      return prev
    })
  }, [
    missingColumns,
    foreignKeyOptions,
    isBranchColumnName,
    isLevelColumnName,
    getSelectedBranchId,
  ])

  // Notify parent after state commits to avoid setState during render in parent
  useEffect(() => {
    if (!onDefaultValuesChangeRef.current) return
    if (!selectedDefaults || Object.keys(selectedDefaults).length === 0) return
    onDefaultValuesChangeRef.current(selectedDefaults)
  }, [selectedDefaults])

  // Fetch import schema from API
  useEffect(() => {
    const handleSuccess = (response: any) => {
      if (response && response.result && Array.isArray(response.result)) {
        // Store the API schema columns which will be added to dropdown options
        // Format: ["user_student.user_student.firstName", "user_teacher.user_teacher.lastName", etc.]
        setApiSchemaColumns(response.result)
      } else {
        console.warn('⚠️ API response format unexpected:', response)
      }
    }

    const handleFailure = (error: any) => {
      console.error('Failed to fetch import schema:', error)
    }

    // Call getCsvImportSchema API to get available database columns
    dispatch(getCsvImportSchema(handleSuccess, handleFailure) as any)
  }, [dispatch])

  // Once schema loads, clear any initial mapping whose target DB column doesn't exist
  // in the schema. e.g. "employee_id" → "user_teacher.id" is invalid when the schema
  // has no "user_teacher.user_teacher.id" entry.
  useEffect(() => {
    if (apiSchemaColumns.length === 0) return

    const validShortKeys = new Set<string>()
    const validFullKeys = new Set<string>(apiSchemaColumns)
    apiSchemaColumns.forEach((col) => {
      const parts = col.split('.')
      if (parts.length >= 3) {
        validShortKeys.add(`${parts[0]}.${parts[parts.length - 1]}`)
      }
    })

    Object.entries(columnMappings).forEach(([csvCol, dbCol]) => {
      if (!dbCol) return
      if (validFullKeys.has(dbCol) || validShortKeys.has(dbCol)) return
      onColumnMappingChange(csvCol, '')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiSchemaColumns])

  const getMatchColor = (match: MatchInfo) => {
    switch (match.confidence) {
      case 'high':
        return 'success'
      case 'medium':
        return 'warning'
      case 'low':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  // Get available database columns for the selected table
  const getAvailableDbColumns = (currentCsvColumn?: string) => {
    const availableColumns: {value: string; label: string; table: string}[] = []

    if (!selectedTable || !schemas[selectedTable]) {
      Object.entries(schemas).forEach(([tableName, tableColumns]) => {
        if (Array.isArray(tableColumns)) {
          tableColumns.forEach((column) => {
            const fullKey = `${tableName}.${column}`
            availableColumns.push({
              value: fullKey,
              label: getColumnDisplayName(fullKey),
              table: tableName,
            })
          })
        }
      })
    } else {
      const tableColumns = schemas[selectedTable] || []
      tableColumns.forEach((column) => {
        const fullKey = `${selectedTable}.${column}`
        availableColumns.push({
          value: fullKey,
          label: getColumnDisplayName(fullKey),
          table: selectedTable,
        })
      })
    }

    // Add API schema columns to the available options
    apiSchemaColumns.forEach((schemaColumn) => {
      // schemaColumn format is like: "user_student.user_student.firstName"
      const parts = schemaColumn.split('.')
      if (parts.length >= 2) {
        const tableName = parts[0]

        // Add all API schema columns regardless of selected table for now
        // This ensures users can see all available options
        const existingColumn = availableColumns.find((col) => col.value === schemaColumn)
        if (!existingColumn) {
          availableColumns.push({
            value: schemaColumn,
            label: getColumnDisplayName(schemaColumn),
            table: tableName,
          })
        }
      }
    })

    // Don't filter out any columns - show all options in all dropdowns
    // Note: user_teacher time columns allow multiple columns to map to the same database column
    const filteredColumns = availableColumns

    // Add any currently mapped column for this specific input column
    const currentMapping = currentCsvColumn ? columnMappings[currentCsvColumn] : null
    if (
      currentMapping &&
      currentMapping !== '' &&
      !filteredColumns.find((col) => col.value === currentMapping)
    ) {
      // Try to determine the table for the current mapping
      let mappingTable = selectedTable || 'unknown'
      if (currentMapping.includes('.')) {
        mappingTable = currentMapping.split('.')[0]
      }

      const fullKey = currentMapping.includes('.')
        ? currentMapping
        : `${mappingTable}.${currentMapping}`
      filteredColumns.push({
        value: currentMapping,
        label: getColumnDisplayName(fullKey),
        table: mappingTable,
      })
    }

    // Sort by table name, then column name
    const sortedColumns = filteredColumns.sort((a, b) => {
      if (a.table !== b.table) {
        return a.table.localeCompare(b.table)
      }
      return a.label.localeCompare(b.label)
    })

    return sortedColumns
  }

  if (csvColumns.length === 0) {
    return <div>{intl.formatMessage({id: 'GENERAL.LOADING'})}</div>
  }

  return (
    <>
      <div id='csv-column-mapper' className='row'>
        <div className='col-12'>
          <h5 id='csv-column-mapper-header' className='mb-4'>
            {intl.formatMessage({id: 'CSV_IMPORT.COLUMN_MAPPING.TITLE'})}
          </h5>

          {/* <Alert variant='info' className='mb-4'>
            <div className='small text-info mt-1'>
              <strong>
                {intl.formatMessage({id: 'CSV_IMPORT.COLUMN_MAPPING.AVAILABLE_TABLES'})}:
              </strong>
              {RESTRICTED_TABLES.join(', ')}
            </div>
          </Alert> */}

          {/* Main Branch auto-creation info - only when there is exactly one default Main Branch */}
          {(() => {
            const branchKey = missingColumns.find((c) => isBranchColumnName(c))
            const branchOptions = branchKey ? foreignKeyOptions[branchKey] || [] : []
            const selectedBranch = branchKey ? selectedDefaults[branchKey] : undefined
            const isOnlyOneMainBranch =
              branchKey &&
              branchOptions.length === 1 &&
              selectedBranch &&
              String(selectedBranch.name || '')
                .trim()
                .toLowerCase() === 'main branch'
            if (branchKey && autoBranchColumns.includes(branchKey)) {
              const only = (foreignKeyOptions[branchKey] || [])[0]
              return (
                <p className='csv-column-mapper__branch-note mb-4'>
                  <i className='fas fa-check-circle me-2' aria-hidden />
                  {intl.formatMessage(
                    {id: 'CSV_IMPORT.COLUMN_MAPPING.SINGLE_BRANCH_INFO'},
                    {branch: only ? formatOptionName(only, branchKey) : ''}
                  )}
                </p>
              )
            }
            return isOnlyOneMainBranch ? (
              <Alert variant='info' className='mb-4'>
                <i className='fas fa-info-circle me-2'></i>
                {intl.formatMessage({id: 'CSV_IMPORT.COLUMN_MAPPING.MAIN_BRANCH_INFO'})}
              </Alert>
            ) : null
          })()}

          {/* Required Data Missing Section */}
          {showRequiredMissingData && askedColumns.length > 0 && (
            <>
              <Alert variant='warning' className='mb-4 csv-import-warning-alert'>
                {(() => {
                  const hasBranchMissing = askedColumns.some((c) => isBranchColumnName(c))
                  const hasLevelMissing = askedColumns.some((c) => isLevelColumnName(c))

                  // Both branch and level missing → a single concise "Action Required"
                  // summary; the per-column dropdowns below handle the specifics.
                  if (hasBranchMissing && hasLevelMissing) {
                    return (
                      <>
                        <Alert.Heading className='h5'>
                          <i className='fas fa-exclamation-triangle me-2'></i>
                          Action Required
                        </Alert.Heading>
                        <p className='mb-2'>
                          Please select a branch and complete the required data before importing
                          your file.
                        </p>
                      </>
                    )
                  }

                  // Branch only → branch-specific guidance, no missing-data list.
                  if (hasBranchMissing) {
                    return (
                      <>
                        <Alert.Heading className='h5'>
                          <i className='fas fa-exclamation-triangle me-2'></i>
                          Branch Selection Required
                        </Alert.Heading>
                        <p className='mb-2'>
                          Please select the branch where you want to import this data.
                        </p>
                        <p className='mb-2'>
                          Choose a branch from the dropdown below to map all records correctly
                          before continuing.
                        </p>
                      </>
                    )
                  }

                  // Level (or other required data) missing → list of missing data
                  // with level-selection guidance.
                  return (
                    <>
                      <Alert.Heading className='h5'>
                        <i className='fas fa-exclamation-triangle me-2'></i>
                        {intl.formatMessage({
                          id: 'CSV_IMPORT.COLUMN_MAPPING.REQUIRED_MISSING_DATA',
                        })}
                      </Alert.Heading>
                      <p className='mb-2'>
                        These fields are required — please select an option from the dropdowns below
                        to continue:
                      </p>
                      <ul className='mb-3'>
                        {askedColumns.map((column, index) => (
                          <li key={index}>
                            <strong>{formatColumnName(column)}</strong>
                            <span className='text-danger ms-1'>*</span>
                          </li>
                        ))}
                      </ul>
                      <p className='mb-2'>
                        Select a level from the dropdown below to import all data into one level.
                      </p>
                      {hasLevelMissing && (
                        <p className='mb-2'>
                          <strong>Multiple Levels:</strong> Add the required levels to your Excel
                          file and re-upload it.
                        </p>
                      )}
                    </>
                  )
                })()}
                <div className='row mt-4'>
                  {askedColumns.map((column) => {
                    const rawOptions = foreignKeyOptions[column] || []
                    const selectedBranchId = getSelectedBranchId(selectedDefaults)
                    const options =
                      isLevelColumnName(column) && selectedBranchId
                        ? rawOptions.filter(
                            (opt) =>
                              opt?.branch_id === selectedBranchId ||
                              opt?.branchId === selectedBranchId ||
                              opt?.branch === selectedBranchId ||
                              opt?.branch?.id === selectedBranchId
                          )
                        : rawOptions
                    const selectedOption = selectedDefaults[column]
                    const hasOptions = options.length > 0
                    const columnMappedFromCsv = !hasOptions && isMissingColumnMapped(column)

                    return (
                      <div key={column} className='col-md-4 mb-3'>
                        <div
                          className={`card h-100 ${
                            !hasOptions
                              ? columnMappedFromCsv
                                ? 'border-success'
                                : 'border-danger'
                              : selectedOption
                                ? 'border-success'
                                : 'border-danger'
                          }`}
                        >
                          <div className='card-body text-center py-3'>
                            <div className='d-flex align-items-center justify-content-center mb-2'>
                              <Badge
                                bg={
                                  columnMappedFromCsv
                                    ? 'success'
                                    : selectedOption
                                      ? 'success'
                                      : hasOptions
                                        ? 'warning'
                                        : 'danger'
                                }
                                className='me-2'
                              >
                                <i
                                  className={
                                    columnMappedFromCsv || selectedOption
                                      ? 'fas fa-check'
                                      : hasOptions
                                        ? 'fas fa-exclamation'
                                        : 'fas fa-times'
                                  }
                                ></i>
                              </Badge>
                              <h6 className='card-title mb-0 text-capitalize'>
                                {formatColumnName(column)}
                                <span className='text-danger ms-1'>*</span>
                              </h6>
                            </div>

                            {hasOptions ? (
                              <>
                                <FilterSelect
                                  className='w-100 mb-2'
                                  triggerClassName='filter-select-sm w-100'
                                  value={selectedOption?.id ? String(selectedOption.id) : ''}
                                  options={[
                                    {
                                      value: '',
                                      label: intl.formatMessage({
                                        id: 'CSV_IMPORT.COLUMN_MAPPING.SELECT_DEFAULT_VALUE',
                                      }),
                                    },
                                    ...options.map((option) => ({
                                      value: String(option.id),
                                      label: formatOptionName(option, column),
                                    })),
                                  ]}
                                  onChange={(val) => {
                                    const option = options.find((opt) => String(opt.id) === val)
                                    if (option) {
                                      handleSelectionChange(column, option)
                                    }
                                  }}
                                  ariaLabel={formatColumnName(column)}
                                />

                                {selectedOption && (
                                  <p className='card-text small text-success mb-0'>
                                    <i className='fas fa-check-circle me-1'></i>
                                    {formatOptionName(selectedOption, column)}
                                  </p>
                                )}
                              </>
                            ) : columnMappedFromCsv ? (
                              <p className='card-text small text-success mb-0'>
                                <i className='fas fa-check-circle me-1'></i>
                                {intl.formatMessage({
                                  id: 'CSV_IMPORT.COLUMN_MAPPING.MAPPED_FROM_CSV',
                                })}
                              </p>
                            ) : (
                              <p className='card-text small text-danger mb-0'>
                                <i className='fas fa-exclamation-triangle me-1'></i>
                                {intl.formatMessage({
                                  id: 'CSV_IMPORT.COLUMN_MAPPING.REQUIRED_FIELD_MAP_BELOW',
                                })}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </Alert>
            </>
          )}

          <div className='table-responsive'>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th style={{width: '30%'}}>
                    {intl.formatMessage({id: 'CSV_IMPORT.COLUMN_MAPPING.CSV_COLUMN'})}
                  </th>
                  <th style={{width: '40%'}}>
                    {intl.formatMessage({id: 'CSV_IMPORT.COLUMN_MAPPING.DATABASE_COLUMN'})}
                  </th>
                  <th style={{width: '20%'}}>
                    {intl.formatMessage({id: 'CSV_IMPORT.COLUMN_MAPPING.MATCH_CONFIDENCE'})}
                  </th>
                  <th style={{width: '10%'}}>
                    {intl.formatMessage({id: 'CSV_IMPORT.COLUMN_MAPPING.STATUS'})}
                  </th>
                </tr>
              </thead>
              <tbody>
                {csvColumns.map((csvCol) => {
                  const match = columnMatches[csvCol]
                  const currentMapping = columnMappings[csvCol]
                  const isMapped = currentMapping && currentMapping !== ''
                  const availableDbColumns = getAvailableDbColumns(csvCol)

                  return (
                    <tr key={csvCol}>
                      <td className='fw-bold'>{csvCol}</td>
                      <td>
                        <FilterSelect
                          className='w-100'
                          triggerClassName='filter-select-sm w-100'
                          value={currentMapping || ''}
                          options={[
                            {
                              value: '',
                              label: intl.formatMessage({
                                id: 'CSV_IMPORT.COLUMN_MAPPING.SELECT_DATABASE_COLUMN',
                              }),
                            },
                            ...availableDbColumns.map((dbCol) => ({
                              value: dbCol.value,
                              label: dbCol.label,
                            })),
                          ]}
                          onChange={(val) => onColumnMappingChange(csvCol, val)}
                          ariaLabel={intl.formatMessage({
                            id: 'CSV_IMPORT.COLUMN_MAPPING.DATABASE_COLUMN',
                          })}
                        />
                      </td>
                      <td>
                        {match && isMapped && (
                          <Badge bg={getMatchColor(match)}>
                            {Math.round(match.similarity * 100)}% ({match.matchType})
                          </Badge>
                        )}
                      </td>
                      <td>
                        {isMapped ? (
                          <span
                            className='badge d-inline-flex align-items-center gap-2'
                            style={{backgroundColor: '#50cd89', color: '#ffffff'}}
                          >
                            <i
                              className='fas fa-check'
                              style={{color: '#ffffff', fontSize: '0.85em'}}
                            ></i>
                            {intl.formatMessage({id: 'CSV_IMPORT.COLUMN_MAPPING.MAPPED'})}
                          </span>
                        ) : (
                          <Badge
                            bg='warning'
                            text='dark'
                            className='d-inline-flex align-items-center gap-2'
                          >
                            <i className='fas fa-exclamation' style={{fontSize: '0.85em'}}></i>
                            {intl.formatMessage({id: 'CSV_IMPORT.COLUMN_MAPPING.UNMAPPED'})}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>

          <div className='row row-cols-1 row-cols-md-3 g-3 mt-4'>
            <div className='col'>
              <div className='card border-success h-100'>
                <div className='card-body text-center py-4'>
                  <h5 className='card-title text-success mb-1'>
                    {Object.values(columnMappings).filter((mapping) => mapping !== '').length}
                  </h5>
                  <p className='card-text small mb-0'>
                    {intl.formatMessage({id: 'CSV_IMPORT.COLUMN_MAPPING.MAPPED_COLUMNS'})}
                  </p>
                </div>
              </div>
            </div>
            <div className='col'>
              <div className='card border-warning h-100'>
                <div className='card-body text-center py-4'>
                  <h5 className='card-title text-warning mb-1'>
                    {Object.values(columnMappings).filter((mapping) => mapping === '').length}
                  </h5>
                  <p className='card-text small mb-0'>
                    {intl.formatMessage({id: 'CSV_IMPORT.COLUMN_MAPPING.UNMAPPED_COLUMNS'})}
                  </p>
                </div>
              </div>
            </div>
            <div className='col'>
              <div className='card border-primary h-100'>
                <div className='card-body text-center py-4'>
                  <h5 className='card-title text-primary mb-1'>{csvColumns.length}</h5>
                  <p className='card-text small mb-0'>
                    {intl.formatMessage({id: 'CSV_IMPORT.COLUMN_MAPPING.TOTAL_COLUMNS'})}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CsvColumnMapper
